import { QueryClient } from "@tanstack/react-query";
import {
  Criterion,
  TestBuyProductWithCriteria,
} from "@coworker/types/lib/testbuy";
import type { Testbuy } from "./testbuy.service";

const TESTBUY_QUERY_KEY = "testbuy_v2";

export function getTestbuyQueryKey(testbuyId: string | null) {
  return [TESTBUY_QUERY_KEY, testbuyId];
}

export function invalidateTestbuyQueries(
  queryClient: QueryClient,
  testbuyId: string
) {
  return queryClient.invalidateQueries({
    queryKey: getTestbuyQueryKey(testbuyId),
  });
}

/**
 * Patches a single criterion into the cached testbuy, avoiding a full refetch of the
 * testbuy (and all its articles/criteria) after every criterion update.
 */
export function patchTestbuyArticleCriterionCache(
  queryClient: QueryClient,
  testbuyId: string,
  articleId: string,
  criterion: Criterion
) {
  queryClient.setQueryData<Testbuy>(getTestbuyQueryKey(testbuyId), (old) => {
    if (!old) return old;
    return {
      ...old,
      articles: old.articles.map((article) =>
        article.id === articleId
          ? {
              ...article,
              criteria: article.criteria.map((c) =>
                c.key === criterion.key ? { ...c, ...criterion } : c
              ),
            }
          : article
      ),
    };
  });
}

/**
 * Replaces an article in the cached testbuy, maintaining its position in the list.
 * Stores the order by shortId, invalidates to get fresh data with proper IDs,
 * then reorders the refetched articles to match the original order.
 */
export async function replaceTestbuyArticleAndRefetch(
  queryClient: QueryClient,
  testbuyId: string,
  oldArticleShortId: string,
  newArticleShortId: string
) {
  // Get current order based on shortIds (which are stable identifiers)
  const currentData = queryClient.getQueryData<Testbuy>(
    getTestbuyQueryKey(testbuyId)
  );
  const originalOrder = currentData?.articles.map((a) => a.shortId) ?? [];

  // Replace old shortId with new one in the order array
  const newOrder = originalOrder.map((shortId) =>
    shortId === oldArticleShortId ? newArticleShortId : shortId
  );

  // Invalidate to get fresh data from server (with proper IDs)
  await queryClient.invalidateQueries({
    queryKey: getTestbuyQueryKey(testbuyId),
  });

  // Reorder the refetched articles to match the original order
  queryClient.setQueryData<Testbuy>(getTestbuyQueryKey(testbuyId), (data) => {
    if (!data) return data;
    const articleMap = new Map(data.articles.map((a) => [a.shortId, a]));
    const orderedArticles = newOrder
      .map((shortId) => articleMap.get(shortId))
      .filter((a): a is TestBuyProductWithCriteria => a !== undefined);

    return {
      ...data,
      articles: orderedArticles,
    };
  });
}
